import React, { useMemo } from "react"
import withRouter from "components/Common/withRouter"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import TableContainer from "components/Common/TableContainer"
import ServerPagination from "components/Common/ServerPagination"
import { Product } from "pages/vendor/Products/Products"
import { ProductActionMenu } from "./Components/ProductActionMenu"
import { useTranslation } from "react-i18next"

interface ProductsTableProps {
  products: Product[]
  isAllChecked: boolean
  checkedProducts: Product[]
  toggleAllProducts: () => void
  toggleActiveProduct: (product: Product) => void
  togglecheckedProductHandler: (product: Product) => void
  editProductHandler: (product: Product) => void
  deleteProductHandler: (product: Product) => void
  viewDetailsHandler: (product: Product) => void
  addNewOffer: (product: Product) => void
  addNewCampaign: (product: Product) => void
  duplicateProductHandler: (product: Product) => void
  pagination?: {
    pageIndex: number
    pageSize: number
    totalCount: number
    totalPages: number
    hasPreviousPage: boolean
    hasNextPage: boolean
  }
  onPageChange?: (pageIndex: number) => void
  location?: any
}

const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  checkedProducts,
  isAllChecked,
  toggleActiveProduct,
  toggleAllProducts,
  togglecheckedProductHandler,
  editProductHandler,
  deleteProductHandler,
  viewDetailsHandler,
  addNewCampaign,
  addNewOffer,
  duplicateProductHandler,
  pagination,
  onPageChange,
  location,
}) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === "ar"

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <input
            type="checkbox"
            className="form-check-input"
            onClick={toggleAllProducts}
            checked={isAllChecked}
          />
        ),
        accessor: "id",
        Cell: (cellProps: { row: { original: Product } }) => {
          return (
            <input
              onClick={() =>
                togglecheckedProductHandler(cellProps.row.original)
              }
              checked={Boolean(
                checkedProducts.find(p => p.id === cellProps.row.original.id)
              )}
              type="checkbox"
              className="form-check-input"
            />
          )
        },
      },
      {
        Header: t("products_products"),
        accessor: "enName",
        filterable: true,
        Cell: (cellProps: { row: { original: Product } }) => {
          const product = cellProps.row.original
          const primaryImage = product.images?.find(img => img.isPrimary || img.isMain)
          const imageUrl = primaryImage?.filePath
            ? `${process.env.REACT_APP_BACKEND_IMAGE_URL}${primaryImage.filePath}`
            : "https://placehold.co/36x36/png"
          return (
            <div className="d-flex justify-content-start align-items-center gap-3">
              <img
                src={imageUrl}
                alt="product"
                className="rounded-circle"
                style={{
                  width: "36px",
                  height: "36px",
                  objectFit: "cover",
                }}
              />
              <div>
                <div className="fw-medium">
                  {isArabic ? product.arName : product.enName}
                </div>
                <small className="text-muted">
                  {isArabic ? product.enName : product.arName}
                </small>
              </div>
            </div>
          )
        },
      },
      {
        Header: t("products_category"),
        accessor: "categoryName",
        filterable: true,
        Cell: (cellProps: { row: { original: Product } }) => {
          const product = cellProps.row.original
          return (
            <div>
              <div className="fw-medium">{product.categoryName}</div>
            </div>
          )
        },
      },
      {
        Header: t("products_couponBased"),
        accessor: "coupon",
        filterable: true,
        Cell: (cellProps: { row: { original: Product } }) => {
          return (
            <>
              <span className="badge fw-semibold bg-primary-30 text-primary me-1 px-2 py-1 font-size-10">
                __
              </span>
            </>
          )
        },
      },
      {
        Header: t("common_price"),
        accessor: "price",
        filterable: true,
        Cell: (cellProps: { row: { original: Product } }) => {
          return <>QAR {cellProps.row.original.price.toFixed(2)}</>
        },
      },
      {
        Header: t("common_size"),
        accessor: "size",
        filterable: true,
        Cell: (cellProps: { row: { original: Product } }) => {
          return <>__</>
        },
      },
      {
        Header: t("products_stock"),
        accessor: "totalAvailableQuantity",
        filterable: true,
        Cell: (cellProps: { row: { original: Product } }) => {
          return <>{cellProps.row.original.totalAvailableQuantity}</>
        },
      },
      {
        Header: t("products_featured"),
        accessor: "featured",
        filterable: true,
        Cell: (cellProps: { row: { original: Product } }) => {
          return (
            <>
              <></>
            </>
          )
        },
      },
      {
        Header: t("common_status"),
        accessor: "isActive",
        filterable: true,
        Cell: (cellProps: { row: { original: Product } }) => {
          return (
            <div className="form-check form-switch mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id={`customSwitch${cellProps.row.original.id}`}
                checked={cellProps.row.original.isActive}
                onClick={() => toggleActiveProduct(cellProps.row.original)}
              />
              <label
                className="form-check-label text-capitalize"
                htmlFor={`customSwitch${cellProps.row.original.id}`}
              >
                {cellProps.row.original.isActive ? "Active" : "Inactive"}
              </label>
            </div>
          )
        },
      },
      {
        Header: " ",
        Cell: (cellProps: { row: { original: Product } }) => {
          const order = cellProps.row.original
          return (
            <ProductActionMenu
              product={order}
              viewDetailsHandler={viewDetailsHandler}
              editProductHandler={editProductHandler}
              deleteProductHandler={deleteProductHandler}
              addNewOffer={addNewOffer}
              addNewCampaign={addNewCampaign}
              duplicateProductHandler={duplicateProductHandler}
            />
          )
        },
      },
    ],
    [products, t, checkedProducts, isArabic]
  )

  // Check if using server-side pagination
  const useServerPagination = pagination && pagination.totalPages > 0

  return (
    <>
      <TableContainer
        columns={columns}
        data={products}
        isGlobalFilter={true}
        isAddOptions={false}
        // When using server pagination, set page size to item count to disable client pagination
        customPageSize={useServerPagination ? products.length : 5}
        isJobListGlobalFilter={undefined}
        isAddUserList={undefined}
        handleOrderClicks={undefined}
        handleUserClick={undefined}
        handleCustomerClick={undefined}
        isAddCustList={undefined}
        className={undefined}
        customPageSizeOptions={undefined}
        // Hide TableContainer pagination when using server pagination
        hidePagination={useServerPagination}
      />

      {/* Server-side pagination - reusable component */}
      {useServerPagination && onPageChange && pagination && (
        <ServerPagination
          pageIndex={pagination.pageIndex}
          totalPages={pagination.totalPages}
          hasPreviousPage={pagination.hasPreviousPage}
          hasNextPage={pagination.hasNextPage}
          onPageChange={onPageChange}
        />
      )}
    </>
  )
}

ProductsTable.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(ProductsTable))
